import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore'
import { db, firebaseEnabled } from './client'
import type { BaseDocument } from '@/types/models'
import { httpsCallable } from 'firebase/functions'
import { functions } from './client'
import { toPlainFirestoreData } from '@/utils/firestoreData'

export async function listDocuments<T extends BaseDocument>(path: string, fallback: T[], orderField?: string): Promise<T[]> {
  if (!firebaseEnabled || !db) return fallback
  const reference = orderField ? query(collection(db, path), orderBy(orderField, 'asc')) : collection(db, path)
  const snapshot = await getDocs(reference)
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as T)
}

export async function listPublicDocuments<T extends BaseDocument>(
  path: string,
  fallback: T[],
  field: string,
  value: string | boolean,
  orderField?: string,
): Promise<T[]> {
  if (!firebaseEnabled || !db) return fallback
  const snapshot = await getDocs(query(collection(db, path), where(field, '==', value)))
  const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as T)
  if (!orderField) return rows
  return rows.sort((left, right) => Number((left as Record<string, unknown>)[orderField] || 0) - Number((right as Record<string, unknown>)[orderField] || 0))
}

export async function getDocument<T>(path: string, id: string, fallback?: T): Promise<T | undefined> {
  if (!firebaseEnabled || !db) return fallback
  const snapshot = await getDoc(doc(db, path, id))
  return snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as T) : undefined
}

export async function saveDocument<T extends Record<string, unknown>>(path: string, id: string | undefined, data: T) {
  if (!firebaseEnabled || !db) throw new Error('Firebase no está configurado. Use el modo demo solo para lectura.')
  const payload = toPlainFirestoreData(data)
  if (id) {
    await setDoc(doc(db, path, id), { ...payload, updatedAt: serverTimestamp() }, { merge: true })
    return id
  }
  const created = await addDoc(collection(db, path), { ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
  return created.id
}

export async function patchDocument(path: string, id: string, data: Record<string, unknown>) {
  if (!db) throw new Error('Firebase no está configurado.')
  await updateDoc(doc(db, path, id), { ...toPlainFirestoreData(data), updatedAt: serverTimestamp() })
}

export async function removeDocument(path: string, id: string) {
  if (!db) throw new Error('Firebase no está configurado.')
  await deleteDoc(doc(db, path, id))
}

type ContentAction = 'save' | 'archive' | 'publish' | 'unpublish'

export async function mutateContent(
  collectionName: string,
  documentId: string | undefined,
  action: ContentAction,
  data: Record<string, unknown> = {},
) {
  if (!functions) throw new Error('Firebase Functions no está configurado.')
  const payload = toPlainFirestoreData(data)
  const callable = httpsCallable<
    { collection: string; documentId?: string; action: ContentAction; data: Record<string, unknown> },
    { id: string }
  >(functions, 'mutateAdminContent')
  const request: { collection: string; documentId?: string; action: ContentAction; data: Record<string, unknown> } = {
    collection: collectionName,
    action,
    data: payload,
  }
  if (documentId) request.documentId = documentId
  const result = await callable(request)
  return result.data.id
}
